import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';

const PROJECTS_QUERY_KEY = ['projects'];
const BOOKMARK_BUTTON_TEST_ID = 'project-card-popover-bookmark';

/** `data-testid` of a project's card in the map list, the same in every section. */
export const mapListCardTestId = (projectId: string) => `home-map-list-view-project-${projectId}`;

/** True for a click the browser synthesized from Enter or Space, which reports no pointer clicks. */
export const isKeyboardActivation = (e: { detail: number }) => e.detail === 0;

/**
 * Returns a function that focuses a project's card in the map list once the projects queries
 * stop fetching, unless the user has already moved focus elsewhere. Bookmarking moves a card to
 * another section, which remounts it, so focus has to be put back on the new card after the
 * refetch renders it. With `{ target: 'bookmark' }` focus goes on to the bookmark button in the
 * card's popover, which opens when the card takes focus. When the card isn't loaded or sits in a
 * collapsed section, focus goes to the first section heading instead.
 */
export function useFocusMapListCard() {
  const queryClient = useQueryClient();
  // Cancels whatever the last call still has pending, a cache subscription or a queued frame, on unmount or a newer call.
  const cancelPendingRef = useRef<() => void>(() => {});

  useEffect(() => () => cancelPendingRef.current(), []);

  return useCallback(
    (projectId: string, { target = 'card' }: { target?: 'card' | 'bookmark' } = {}) => {
      cancelPendingRef.current();
      const queueFrame = (run: () => void) => {
        const frame = requestAnimationFrame(() => {
          cancelPendingRef.current = () => {};
          run();
        });
        cancelPendingRef.current = () => {
          cancelAnimationFrame(frame);
          cancelPendingRef.current = () => {};
        };
      };
      const focusBookmark = (card: HTMLElement) => {
        if (document.activeElement !== card) return;
        const popoverId = card.getAttribute('aria-controls');
        const popover = popoverId ? document.getElementById(popoverId) : null;
        popover?.querySelector<HTMLElement>(`[data-testid="${BOOKMARK_BUTTON_TEST_ID}"]`)?.focus();
      };
      const focusCard = () =>
        queueFrame(() => {
          const active = document.activeElement;
          if (active && active !== document.body) return;
          const card = document.querySelector<HTMLElement>(
            `[role="button"][data-testid="${mapListCardTestId(projectId)}"]`,
          );
          if (!card || card.closest('[inert]')) {
            document.querySelector<HTMLElement>('#projects-list [aria-controls^="collapsible-content-"]')?.focus();
            return;
          }
          card.focus();
          // The popover renders after the card's focus handler opens it.
          if (target === 'bookmark') queueFrame(() => focusBookmark(card));
        });
      const isIdle = () => queryClient.isFetching({ queryKey: PROJECTS_QUERY_KEY }) === 0;

      if (isIdle()) {
        focusCard();
        return;
      }
      const unsubscribe = queryClient.getQueryCache().subscribe(() => {
        if (!isIdle()) return;
        unsubscribe();
        focusCard();
      });
      cancelPendingRef.current = () => {
        unsubscribe();
        cancelPendingRef.current = () => {};
      };
    },
    [queryClient],
  );
}
